import axios, { AxiosError } from 'axios';

import { METRICS_SESSION_KEY } from 'src/containers/metrics/constants';
import config from 'src/utils/config';

export interface MonitoringLoginResponse {
  token: string;
  tokenType: 'Bearer';
  expiresAt: string;
}

export interface MonitoringSessionResponse {
  valid: boolean;
  expiresAt: string;
}

export interface MonitoringSession {
  token: string;
  expiresAt: string;
}

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

export const setMonitoringUnauthorizedHandler = (
  handler: UnauthorizedHandler | null
) => {
  onUnauthorized = handler;
};

export const getMetricsSession = (): MonitoringSession | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = sessionStorage.getItem(METRICS_SESSION_KEY);
  if (!raw) {
    return null;
  }

  try {
    const session = JSON.parse(raw) as MonitoringSession;
    if (!session?.token) {
      return null;
    }
    return session;
  } catch {
    sessionStorage.removeItem(METRICS_SESSION_KEY);
    return null;
  }
};

export const setMetricsSession = (session: MonitoringSession) => {
  sessionStorage.setItem(METRICS_SESSION_KEY, JSON.stringify(session));
};

export const clearMetricsSession = () => {
  sessionStorage.removeItem(METRICS_SESSION_KEY);
};

export const monitoringAPI = axios.create({
  baseURL: config.monitoringApiUrl,
  headers: {
    Accept: 'application/json',
  },
});

monitoringAPI.interceptors.request.use((request) => {
  const session = getMetricsSession();
  if (session?.token) {
    request.headers.common.Authorization = `Bearer ${session.token}`;
  }
  return request;
});

monitoringAPI.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    const isLogin = requestUrl.includes('/auth/login');

    if (status === 401 && !isLogin) {
      clearMetricsSession();
      onUnauthorized?.();
    }

    return Promise.reject(error);
  }
);

export const getMonitoringErrorMessage = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return 'Unable to reach the monitoring API.';
  }

  const status = error.response?.status;
  const message = error.response?.data?.message;
  const retryAfter = error.response?.headers?.['retry-after'];

  if (status === 401) {
    return typeof message === 'string' ? message : 'Incorrect password.';
  }

  if (status === 429) {
    return retryAfter
      ? `Too many attempts. Try again in ${retryAfter} seconds.`
      : 'Too many attempts. Try again later.';
  }

  if (typeof message === 'string' && message) {
    return message;
  }

  return 'Unable to reach the monitoring API.';
};

export const loginMonitoring = async (
  password: string
): Promise<MonitoringLoginResponse> => {
  clearMetricsSession();

  const { data } = await monitoringAPI.post<MonitoringLoginResponse>(
    '/auth/login',
    { password }
  );

  setMetricsSession({
    token: data.token,
    expiresAt: data.expiresAt,
  });

  return data;
};

export const fetchMonitoringSession =
  async (): Promise<MonitoringSessionResponse> => {
    const { data } = await monitoringAPI.get<MonitoringSessionResponse>(
      '/auth/session'
    );
    return data;
  };
